import {
  EuiButton,
  EuiContextMenu,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiInputPopover,
  EuiToken,
} from "@inscopix/ideas-eui";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { ForwardedRef, forwardRef, useEffect, useState } from "react";
import { COLUMN_KIND_MAP } from "../ModalCreateColumn/ModalCreateColumn";
import { isDefined } from "utils/isDefined";
import { keyBy } from "lodash";
import {
  DataTableColumnDefinition,
  DataTableStoreState,
} from "pages/gdt/store/DataTableProvider.types";
import { chain } from "lodash";
import { isNonNullish } from "utils/isNonNullish";

export type FieldColumnProps = {
  autoFocus?: boolean;
  disabled?: boolean;
  helpText?: string;
  filter?: {
    includeColumn?: (
      column: DataTableStoreState["tables"][number]["columns"][number],
    ) => boolean;
    tableId?: string;
  };
  label?: string;
  value: string | undefined;
  onChange: (value: {
    id: string;
    name: string;
    kind: "data_table_column" | "gdt_analysis_table_column";
    order: number;
    definition: DataTableColumnDefinition;
    table: {
      id: string;
      key: string;
    };
  }) => void;
};

export const FieldColumn = forwardRef(function FieldColumn(
  {
    autoFocus = false,
    disabled = false,
    helpText,
    filter,
    label = "Column",
    value,
    onChange,
  }: FieldColumnProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const tables = useDataTableContext((s) => s.tables);
  const [isPopoverOpen, setIsPopoverOpen] = useState(autoFocus);
  const [tableId, setTableId] = useState(filter?.tableId);
  const columnsById = useDataTableContext((s) => {
    const allColumns = s.tables.flatMap((table) => table.columns);
    return keyBy(allColumns, (column) => column.id);
  });
  const column = isDefined(value) ? columnsById[value] : undefined;
  const groupsById = useDataTableContext((s) => {
    return chain(s.tables)
      .flatMap((table) => table.columnGroups)
      .keyBy((group) => group.id)
      .value();
  });
  const togglePopover = () => setIsPopoverOpen((isOpen) => !isOpen);
  const closePopover = () => setIsPopoverOpen(false);

  useEffect(() => {
    setTableId(filter?.tableId);
  }, [filter?.tableId]);

  const panels = [
    {
      id: 0,
      title: "Tables",
      width: "100%",
      items: tables
        .filter((table) => {
          const tableIdFilter = filter?.tableId;
          return isDefined(tableIdFilter) ? table.id === tableIdFilter : true;
        })
        .map((table) => ({
          name: (
            <span>
              {table.name} <span style={{ color: "grey" }}>({table.key})</span>
            </span>
          ),
          icon:
            table.kind === "analysis" ? "indexMapping" : "tableDensityNormal",
          panel: table.id,
        })),
    },
    ...tables.map((table) => ({
      id: table.id,
      width: "100%",
      title: (
        <span>
          {table.name} <span style={{ color: "grey" }}>({table.key})</span>
        </span>
      ),
      items: table.columns
        .filter((column) => {
          return filter?.includeColumn !== undefined
            ? filter.includeColumn(column)
            : true;
        })
        .map((column) => ({
          name: isNonNullish(column.group)
            ? `${groupsById[column.group].name} > ${column.name}`
            : column.name,
          icon: (
            <EuiToken iconType={COLUMN_KIND_MAP[column.definition.kind].icon} />
          ),
          onClick: () => {
            setTableId(table.id);
            onChange({
              id: column.id,
              name: column.name,
              order: column.order,
              kind:
                table.kind === "data"
                  ? "data_table_column"
                  : "gdt_analysis_table_column",
              definition: column.definition,
              table,
            });
            closePopover();
          },
        })),
    })),
  ];

  return (
    <EuiFormRow fullWidth label={label} helpText={helpText}>
      <EuiInputPopover
        fullWidth
        autoFocus={autoFocus}
        isOpen={isPopoverOpen}
        closePopover={closePopover}
        panelPaddingSize="none"
        anchorPosition="downLeft"
        input={
          <EuiButton
            buttonRef={ref}
            iconSide="right"
            iconType="arrowDown"
            onClick={togglePopover}
            fullWidth
            color="text"
            style={{
              backgroundColor: "#fbfcfd",
              border: "1px solid #e3e6f1",
            }}
            disabled={disabled}
          >
            {isDefined(column) && (
              <span
                style={{
                  fontWeight: 400,
                  marginRight: "auto",
                  fontFamily:
                    '"Inter UI", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                }}
              >
                <EuiFlexGroup
                  alignItems="center"
                  gutterSize="s"
                  responsive={false}
                >
                  <EuiFlexItem grow={false}>
                    <EuiToken
                      iconType={COLUMN_KIND_MAP[column.definition.kind].icon}
                    />
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    {isNonNullish(column.group)
                      ? `${groupsById[column.group].name} > ${column.name}`
                      : column.name}
                  </EuiFlexItem>
                </EuiFlexGroup>
              </span>
            )}

            {value === undefined && (
              <span
                style={{
                  color: "#98A2B3",
                  fontWeight: 400,
                  marginRight: "auto",
                  fontFamily:
                    '"Inter UI", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                  textDecorationLine: "none !important",
                }}
              >
                Select a column...
              </span>
            )}
          </EuiButton>
        }
      >
        <EuiContextMenu initialPanelId={tableId ?? 0} panels={panels} />
      </EuiInputPopover>
    </EuiFormRow>
  );
});
